import { ViinexHlsLoader } from '@/services/viinex-hls-loader';
import { STREAMING_TIMING } from '@/constants/time';
import type {
    LoaderCallbacks,
    LoaderConfiguration,
    PlaylistLoaderConstructor,
    PlaylistLoaderContext,
} from 'hls.js';

const TARGET_DURATION_SECONDS = 6;
const CONTINUATION_WINDOW_MS = TARGET_DURATION_SECONDS * 1000;

interface ParsedSegment {
    pdt: string;
    extinf: string;
    url: string;
    duration: number;
    discontinuity: boolean;
}

function getPdtTimestamp(pdt: string): number {
    return new Date(pdt.replace('#EXT-X-PROGRAM-DATE-TIME:', '')).getTime();
}

function parseSegments(m3u8: string): ParsedSegment[] {
    const lines = m3u8.split('\n').map((l) => l.trim());
    const segments: ParsedSegment[] = [];
    let pdt: string | null = null;

    for (let i = 0; i < lines.length; i++) {
        if (lines[i].startsWith('#EXT-X-PROGRAM-DATE-TIME:')) {
            pdt = lines[i];
        } else if (lines[i].startsWith('#EXTINF:')) {
            const url = lines[i + 1]?.trim();
            if (url && !url.startsWith('#') && pdt) {
                const duration = parseFloat(
                    lines[i].match(/#EXTINF:([\d.]+)/)?.[1] || '0',
                );
                segments.push({
                    pdt,
                    extinf: lines[i],
                    url,
                    duration,
                    discontinuity: false,
                });
                pdt = null;
                i++;
            }
        }
    }

    return segments;
}

function mergeSegments(
    orderedSegments: ParsedSegment[],
    knownKeys: Map<string, number>,
    newSegments: ParsedSegment[],
): void {
    for (const seg of newSegments) {
        const existingIndex = knownKeys.get(seg.pdt);
        if (existingIndex === undefined) {
            knownKeys.set(seg.pdt, orderedSegments.length);
            orderedSegments.push(seg);
        } else if (seg.duration > orderedSegments[existingIndex].duration) {
            seg.discontinuity = orderedSegments[existingIndex].url !== seg.url;
            orderedSegments[existingIndex] = seg;
        }
    }
}

function segmentEndTime(seg: ParsedSegment): number {
    return getPdtTimestamp(seg.pdt) + seg.duration * 1000;
}

function buildLivePlaylist(segments: ParsedSegment[]): string {
    const targetDuration = Math.ceil(
        Math.max(
            TARGET_DURATION_SECONDS,
            ...segments.map((segment) => segment.duration),
        ),
    );
    let m3u8 = '#EXTM3U\n';
    m3u8 += '#EXT-X-VERSION:3\n';
    m3u8 += `#EXT-X-TARGETDURATION:${targetDuration}\n`;
    m3u8 += '#EXT-X-MEDIA-SEQUENCE:0\n';
    m3u8 += '#EXT-X-INDEPENDENT-SEGMENTS\n';

    for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        if (seg.discontinuity) {
            m3u8 += '#EXT-X-DISCONTINUITY\n';
        }
        m3u8 += seg.pdt + '\n';
        m3u8 += seg.extinf + '\n';
        m3u8 += seg.url + '\n';
    }

    return m3u8;
}

function flushCoalesced(
    inflightPlaylists: Map<string, LoaderCallbacks<PlaylistLoaderContext>[]>,
    key: string,
    notify: (cb: LoaderCallbacks<PlaylistLoaderContext>) => void,
): void {
    const waiting = inflightPlaylists.get(key) ?? [];
    inflightPlaylists.delete(key);
    waiting.forEach(notify);
}

export function createContinuationLoader(
    streamBaseUrl: string,
    initialBeginMs: number,
): PlaylistLoaderConstructor {
    let nextBegin = initialBeginMs;
    let nextEnd = initialBeginMs + STREAMING_TIMING.DEFAULT_VOD_WINDOW_MS;
    const orderedSegments: ParsedSegment[] = [];
    const knownSegmentKeys = new Map<string, number>();
    const inflightPlaylists = new Map<
        string,
        LoaderCallbacks<PlaylistLoaderContext>[]
    >();

    class ContinuationLoader extends ViinexHlsLoader<PlaylistLoaderContext> {
        private _coalescingKey: string | null = null;

        load(
            context: PlaylistLoaderContext,
            config: LoaderConfiguration,
            callbacks: LoaderCallbacks<PlaylistLoaderContext>,
        ) {
            const requestKey = `${nextBegin}:${nextEnd}`;
            context.url = `${streamBaseUrl}/stream.m3u8?begin=${nextBegin}&end=${nextEnd}`;

            if (inflightPlaylists.has(requestKey)) {
                inflightPlaylists.get(requestKey)!.push(callbacks);
                return;
            }

            inflightPlaylists.set(requestKey, []);
            this._coalescingKey = requestKey;

            const originalOnSuccess = callbacks.onSuccess;
            callbacks.onSuccess = (response, stats, ctx, networkDetails) => {
                if (typeof response.data === 'string') {
                    const newSegments = parseSegments(response.data);
                    mergeSegments(
                        orderedSegments,
                        knownSegmentKeys,
                        newSegments,
                    );

                    if (newSegments.length > 0) {
                        const firstEnd = segmentEndTime(newSegments[0]);
                        const lastSegment = newSegments[newSegments.length - 1];
                        const candidateEnd =
                            segmentEndTime(lastSegment) +
                            CONTINUATION_WINDOW_MS;

                        if (candidateEnd > nextEnd) {
                            nextEnd = candidateEnd;
                            // Keep the final segment in range while Viinex may still extend it.
                            nextBegin = Math.min(
                                firstEnd,
                                getPdtTimestamp(lastSegment.pdt),
                            );
                        }
                    }

                    response.data = buildLivePlaylist(orderedSegments);
                }
                originalOnSuccess(response, stats, ctx, networkDetails);
                flushCoalesced(inflightPlaylists, requestKey, (cb) =>
                    cb.onSuccess(response, stats, ctx, networkDetails),
                );
            };

            const originalOnError = callbacks.onError;
            callbacks.onError = (response, ctx, networkDetails, stats) => {
                originalOnError(response, ctx, networkDetails, stats);
                flushCoalesced(inflightPlaylists, requestKey, (cb) =>
                    cb.onError(response, ctx, networkDetails, stats),
                );
            };

            const originalOnTimeout = callbacks.onTimeout;
            callbacks.onTimeout = (stats, ctx, networkDetails) => {
                originalOnTimeout(stats, ctx, networkDetails);
                flushCoalesced(inflightPlaylists, requestKey, (cb) =>
                    cb.onTimeout(stats, ctx, networkDetails),
                );
            };

            super.load(context, config, callbacks);
        }

        abort() {
            if (this._coalescingKey) {
                flushCoalesced(inflightPlaylists, this._coalescingKey, (cb) =>
                    cb.onError(
                        { code: 0, text: 'aborted' },
                        null as any,
                        null,
                        null as any,
                    ),
                );
                this._coalescingKey = null;
            }
            super.abort();
        }
    }

    return ContinuationLoader as unknown as PlaylistLoaderConstructor;
}
